import { z } from 'zod';
import { profileSchema } from './profile.js';
/**
 * セッショントークンの接頭辞（ADR-022）。
 *
 * 漏れたトークンを秘密情報のスキャナで見つけやすくするために付ける。サーバーは接頭辞と長さで
 * 形を確かめてから D1 を引く（形の違う値で D1 を叩かせない）。
 */
export const SESSION_TOKEN_PREFIX = 'dgs_';
/** 32 バイトの乱数を base64url（パディングなし）にした長さ。 */
export const SESSION_TOKEN_RANDOM_LENGTH = 43;
/**
 * API のセッションを渡した結果。ゲストの作成（`POST /api/v1/auth/guest`、#118）と、前のアカウントへの復元
 * （`POST /api/v1/auth/restore` / `link`、#119）で同じ形を返す。
 *
 * **トークンが返るのはこの 1 回だけ。** サーバーはハッシュしか持たないので、クライアントが失くすと
 * そのトークンでは二度と入れない（前のアカウントに戻るには引き継ぎを使う）。
 *
 * iOS が同じ形を `Codable` で読むので、契約テスト（`packages/contracts/mobile/`）がこのスキーマで
 * サーバーの出力を縛る（ADR-015）。
 */
export const sessionGrantSchema = z.object({
    /** `Authorization: Bearer <token>` で送る。 */
    token: z
        .string()
        .startsWith(SESSION_TOKEN_PREFIX)
        .length(SESSION_TOKEN_PREFIX.length + SESSION_TOKEN_RANDOM_LENGTH),
    user: z.object({
        id: z.string().min(1),
    }),
});
/**
 * 引き継ぎに使ったログインの方法（#119、ADR-022）。**表示にだけ使う**（設定の画面の「Apple で設定済み」）。
 *
 * クライアントの申告なので、判定には使わない。
 */
export const LINK_METHODS = ['apple', 'google', 'email'];
const clerkTokenSchema = z.string().min(1).max(4096);
/**
 * 引き継ぎを設定する（`POST /api/v1/auth/link`、#119）。ゲストのセッションで呼ぶ。
 *
 * **省略可能なキーは `null` ではなく省略で送る**（zod の `optional()` は `null` を受け付けない）。
 */
export const linkAccountSchema = z.object({
    /** Clerk のセッションの JWT。サインインした直後に取る（有効期限は 1 分）。 */
    clerkToken: clerkTokenSchema,
    method: z.enum(LINK_METHODS),
    /**
     * このログインが前のアカウントに結びついていて、今のゲストにキャラクターがあるとき、ゲストのデータを消して
     * 前のアカウントに戻すか。確認の画面で同意したときだけ true で送り直す（ADR-022）。
     */
    discardGuestData: z.boolean().optional(),
});
/** 引き継ぎの設定の結果。 */
export const linkResultSchema = z.discriminatedUnion('outcome', [
    /** 今のアカウントに結びつけた（すでに同じログインに結びついていた場合も）。 */
    z.object({ outcome: z.literal('linked') }),
    /** このログインは前のアカウントに結びついていたので、そちらに戻した。**新しいトークンに置き換える。** */
    sessionGrantSchema.extend({ outcome: z.literal('restored') }),
]);
/**
 * 前のアカウントに戻る（`POST /api/v1/auth/restore`、#119）。新しい端末などで、トークンが無いときに呼ぶ（認証なし）。
 */
export const restoreAccountSchema = linkAccountSchema.omit({ discardGuestData: true });
/**
 * AI 鑑定の同意の版（#121、ADR-022）。**説明の文面を変えたら上げる。**
 *
 * サーバーは `users.ai_consent_version` がこれと一致するときだけ鑑定を通す。上げると、既に同意した人からも
 * もう一度取り直す。日付にしているのは、文面を変えた日が分かるようにするため。
 *
 * `2026-09-20`: 手描きのアバター（#169）も AI に送るようにしたので、説明を「選手とアバターの絵」に広げた。
 */
export const AI_CONSENT_VERSION = '2026-09-20';
/** AI 鑑定に同意する（`POST /api/v1/me/consents/ai`、#121）。 */
export const aiConsentSchema = z.object({
    /**
     * 同意した説明の版。**クライアントが読んだ版を送る。**
     *
     * サーバーは今の版と違えば断る。アプリを更新しないまま説明が変わったとき、古い文面に同意した記録を
     * 新しい版として残さないため。
     */
    version: z.string().min(1).max(32),
});
/**
 * アカウントを削除した結果（`DELETE /api/v1/me`、#120）。
 *
 * **中身は `deleted: true` だけ。** Clerk のユーザー削除や Apple の revoke に失敗しても 200 を返す
 * （自前のデータはもう消えていて、ユーザーにできることが無い）。失敗はサーバーのログに残す。
 */
export const accountDeletionSchema = z.object({
    deleted: z.literal(true),
});
/** 自分のアカウント（`GET /api/v1/me`、#119）。 */
export const accountSchema = z.object({
    id: z.string().min(1),
    /** 引き継ぎを設定しているか。 */
    linked: z.boolean(),
    /** 引き継ぎに使ったログインの方法（表示用）。設定していなければ省略。 */
    linkMethod: z.enum(LINK_METHODS).optional(),
    /**
     * 同意済みの AI 鑑定の説明の版（#121）。まだ同意していなければ省略。
     *
     * **アプリは絵を送る前にこれを見る。** `analyze` の 403 で弾くだけだと、同意を得る前に絵が
     * サーバーへ届いてしまう（Guidelines 5.1.2(i) は「渡す前に許可を得る」ことを求めている）。
     */
    aiConsentVersion: z.string().optional(),
    /** ニックネームとアバター（#169）。 */
    profile: profileSchema,
});
